import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as Tooltip from "@radix-ui/react-tooltip";
import { ArrowLeftRight, ChevronDown, ChevronRight, Grid3x3, Search, X } from "lucide-react";
import { command } from "../core";
import { useWorkspace } from "../react/providers/WorkspaceProvider";
import { useWorkspaceNavigation } from "../react/navigation/useWorkspaceNavigation";
import { useCommand } from "../react/hooks/useCommand";
import { appIcon } from "./icons";
import { buildAppSearchIndex, findCurrentAppId, firstActionKey } from "./workspace-utils";
import type { AppSearchItem } from "./workspace-utils";

// Apps + searchable menu index. No query → 4-col grid; query → breadcrumb results
// with ↑/↓/Enter keyboard nav. Matches the canonical AppSwitcher.
export function AppSwitcher() {
    const boot = useWorkspace();
    const dispatch = useCommand();
    const { stack } = useWorkspaceNavigation();

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const resultsRef = useRef<HTMLDivElement>(null);

    // Tooltip suppression for ~700ms after the popover closes — outside-click
    // returns focus / pointer near the trigger and would otherwise cause Radix
    // Tooltip to re-open immediately. Tracking previous open via a ref so this
    // only fires on true → false transitions.
    const wasOpen = useRef(false);
    const [tooltipSuppressed, setTooltipSuppressed] = useState(false);
    // Keep the tooltip ALWAYS controlled (a stable boolean for the
    // component's lifetime). We mirror Radix's hover-driven open state into
    // `tipOpen` via onOpenChange, then force `false` while the menu is open
    // or inside the post-close suppression window. Switching between a
    // boolean and `undefined` is what triggers React's controlled↔uncontrolled
    // warning, so we never hand Radix `undefined`.
    const [tipOpen, setTipOpen] = useState(false);
    useEffect(() => {
        if (open) {
            wasOpen.current = true;
            return;
        }
        if (!wasOpen.current) return;
        wasOpen.current = false;
        setTooltipSuppressed(true);
        const t = setTimeout(() => setTooltipSuppressed(false), 700);
        return () => clearTimeout(t);
    }, [open]);

    // Dismiss the "Switch application" tooltip on ANY pointer-down elsewhere.
    // Radix keeps a hover-opened tooltip up until the pointer leaves the
    // trigger; a click that navigates away (e.g. onto the Leads menu) doesn't
    // count as "leave", so the tooltip lingers over the nav and hides items.
    // Force it closed on any outside click (capture phase, before navigation).
    const triggerRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        const onPointerDown = (e: PointerEvent) => {
            if (triggerRef.current?.contains(e.target as Node)) return;
            setTipOpen(false);
            setTooltipSuppressed(true);
            window.setTimeout(() => setTooltipSuppressed(false), 350);
        };
        document.addEventListener("pointerdown", onPointerDown, true);
        return () => document.removeEventListener("pointerdown", onPointerDown, true);
    }, []);

    // Also dismiss on any NAVIGATION change (a menu was opened). This catches
    // programmatic / keyboard navigation that never emits a pointer event, so
    // the tooltip can never linger over the freshly loaded screen's nav.
    const navKey = stack[0]?.actionKey;
    useEffect(() => {
        setTipOpen(false);
        setTooltipSuppressed(true);
        const t = setTimeout(() => setTooltipSuppressed(false), 350);
        return () => clearTimeout(t);
    }, [navKey]);

    const index = useMemo(() => buildAppSearchIndex(boot), [boot]);
    const currentAppId = findCurrentAppId(boot, stack[0].actionKey) ?? boot.apps[0]?.id;
    const current = boot.apps.find((a) => a.id === currentAppId) ?? boot.apps[0];

    const filtered = useMemo<AppSearchItem[] | null>(() => {
        const q = query.trim().toLowerCase();
        if (!q) return null;
        return index.filter((item) => item.path.join(" › ").toLowerCase().includes(q));
    }, [query, index]);

    useEffect(() => setActiveIndex(0), [query]);
    useEffect(() => {
        if (!open) return;
        const t = setTimeout(() => inputRef.current?.focus(), 30);
        return () => clearTimeout(t);
    }, [open]);
    useEffect(() => {
        if (!filtered?.length) return;
        resultsRef.current
            ?.querySelector<HTMLElement>(`[data-result-index="${activeIndex}"]`)
            ?.scrollIntoView({ block: "nearest" });
    }, [activeIndex, filtered]);

    const openApp = (appId: string) => {
        const key = firstActionKey(boot.menus[appId] ?? []);
        if (key) void dispatch(command("OPEN_ACTION", { actionKey: key }));
        setOpen(false);
    };
    const openItem = (item: AppSearchItem) => {
        if (item.actionKey) {
            void dispatch(command("OPEN_ACTION", { actionKey: item.actionKey }));
            setOpen(false);
        } else {
            openApp(item.appId);
        }
    };
    const onInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            e.stopPropagation();
            if (filtered?.length) setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            e.stopPropagation();
            if (filtered?.length) setActiveIndex((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            if (filtered?.length) openItem(filtered[activeIndex] ?? filtered[0]);
        } else if (e.key !== "Escape") {
            e.stopPropagation();
        }
    };

    if (!current) return null;
    const CurrentIcon = appIcon(current.icon);

    return (
        <DropdownMenu.Root
            open={open}
            onOpenChange={(o) => {
                setOpen(o);
                if (!o) setQuery("");
            }}
        >
            <Tooltip.Provider delayDuration={350}>
                <Tooltip.Root open={open || tooltipSuppressed ? false : tipOpen} onOpenChange={setTipOpen}>
                    <Tooltip.Trigger asChild>
                        <DropdownMenu.Trigger asChild>
                            <button ref={triggerRef} className="switcher__trigger" aria-label={`Switch app — currently ${current.name}`} data-viewid="ede-appswitcher-toggle">
                                <span className="switcher__trigger-icon" data-color={current.color ?? 10}>
                                    <CurrentIcon size={18} />
                                </span>
                                <span className="switcher__trigger-info">
                                    <span className="switcher__trigger-label">{current.name}</span>
                                    <span className="switcher__trigger-sub">
                                        <ArrowLeftRight size={10} />
                                        App
                                    </span>
                                </span>
                                <ChevronDown size={14} className="switcher__trigger-chevron" />
                            </button>
                        </DropdownMenu.Trigger>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                        <Tooltip.Content className="rdx-tooltip" side="bottom" align="start" sideOffset={8}>
                            Switch application
                            <Tooltip.Arrow className="rdx-tooltip__arrow" />
                        </Tooltip.Content>
                    </Tooltip.Portal>
                </Tooltip.Root>
            </Tooltip.Provider>

            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    className="rdx-menu app-switcher-menu"
                    align="start"
                    sideOffset={6}
                    onCloseAutoFocus={(e) => {
                        // Don't return focus to the trigger on close. Focus-return
                        // both paints the button's focus outline (the lingering
                        // "border") AND re-opens the hover/focus tooltip over the
                        // freshly-loaded app. After switching apps focus belongs to
                        // the new screen, not the switcher.
                        e.preventDefault();
                        setTipOpen(false);
                        triggerRef.current?.blur();
                    }}
                >
                    <div className="app-switcher-menu__head">
                        <Search size={14} className="app-switcher-menu__search-icon" />
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            placeholder="Search apps and menus…"
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={onInputKeyDown}
                        />
                        {query ? (
                            <button
                                type="button"
                                className="app-switcher-menu__search-clear"
                                aria-label="Clear search"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setQuery("");
                                    inputRef.current?.focus();
                                }}
                            >
                                <X size={12} />
                            </button>
                        ) : null}
                    </div>

                    {filtered === null ? (
                        <div className="app-grid app-grid--4">
                            {boot.apps.map((app) => {
                                const Icon = appIcon(app.icon);
                                return (
                                    <DropdownMenu.Item
                                        key={app.id}
                                        className={`app-grid-tile ${app.id === current.id ? "is-active" : ""}`}
                                        onSelect={() => openApp(app.id)}
                                        data-viewid="ede-appswitcher-app"
                                        data-app-id={app.id}
                                    >
                                        <span className="app-grid-tile__icon" data-color={app.color ?? 10}>
                                            <Icon size={18} />
                                        </span>
                                        <span className="app-grid-tile__label">{app.name}</span>
                                    </DropdownMenu.Item>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="app-search-results" ref={resultsRef}>
                            {filtered.length === 0 ? (
                                <div className="app-search-results__empty">No matches for “{query}”</div>
                            ) : (
                                filtered.map((item, i) => {
                                    const itemApp = boot.apps.find((a) => a.id === item.appId);
                                    const Icon = appIcon(itemApp?.icon);
                                    return (
                                        <button
                                            type="button"
                                            key={i}
                                            data-result-index={i}
                                            className={`app-search-result ${i === activeIndex ? "is-active" : ""}`}
                                            onClick={() => openItem(item)}
                                            onMouseMove={() => {
                                                if (i !== activeIndex) setActiveIndex(i);
                                            }}
                                        >
                                            <span className="app-search-result__icon" data-color={itemApp?.color ?? 10}>
                                                <Icon size={14} />
                                            </span>
                                            <span className="app-search-result__path">
                                                {item.path.map((part, idx) => (
                                                    <Fragment key={idx}>
                                                        {idx > 0 ? (
                                                            <ChevronRight size={11} className="app-search-result__sep" />
                                                        ) : null}
                                                        <span
                                                            className={
                                                                idx === item.path.length - 1
                                                                    ? "app-search-result__leaf"
                                                                    : "app-search-result__crumb"
                                                            }
                                                        >
                                                            {part}
                                                        </span>
                                                    </Fragment>
                                                ))}
                                            </span>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    )}

                    <DropdownMenu.Separator className="rdx-menu__sep" />
                    <DropdownMenu.Item className="rdx-menu__item">
                        <Grid3x3 size={14} className="rdx-menu__item-icon" />
                        <span className="rdx-menu__item-primary">Browse all installed modules →</span>
                    </DropdownMenu.Item>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}
